import { RevealItem, ScrollReveal } from '#/components/common/ScrollReveal'
import { TextAnimate } from '#/components/common/TextAnimate'
import { HighlightText } from '#/components/common/HighlightText'
import headerIcon from '#/assets/header-icon.svg'
import { COURSE_WHY } from '#/data/courseDetails'
import { LucideCheckCircle2 } from 'lucide-react'

export default function CourseWhySection() {
  return (
    <section
      className="relative w-full py-20 overflow-hidden bg-white"
      id="why-this-course"
    >
      {/* Subtle background accents */}
      <div
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            'radial-gradient(60% 50% at 90% 10%, rgba(255,182,6,0.06) 0%, transparent 70%), radial-gradient(50% 60% at 5% 90%, rgba(0,77,64,0.06) 0%, transparent 70%)',
        }}
      />

      <div className="container mx-auto flex flex-col gap-14">
        {/* Header */}
        <ScrollReveal className="flex flex-col items-center justify-center gap-4 text-center">
          <RevealItem>
            <img alt="" src={headerIcon} />
          </RevealItem>
          <RevealItem>
            <h3 className="text-sm font-semibold uppercase tracking-[0.3em] text-primary-700">
              {COURSE_WHY.eyebrow}
            </h3>
          </RevealItem>
          <RevealItem className="w-full">
            <h2 className="text-4xl font-bold text-neutral-900 sm:text-5xl">
              {COURSE_WHY.title}
              <HighlightText
                inView
                text={COURSE_WHY.highlight}
                className="bg-linear-to-r from-primary-100/70 to-primary-300/70 text-neutral-900"
              />
            </h2>
          </RevealItem>
          <RevealItem className="max-w-2xl">
            <TextAnimate
              by="word"
              animation="fadeIn"
              duration={1.1}
              className="text-base text-neutral-500 sm:text-lg"
            >
              {COURSE_WHY.description}
            </TextAnimate>
          </RevealItem>
        </ScrollReveal>

        {/* Reasons — 2-col alternating layout */}
        <ScrollReveal
          className="grid grid-cols-1 gap-6 md:grid-cols-2"
          stagger={0.14}
        >
          {COURSE_WHY.reasons.map((reason, idx) => (
            <RevealItem key={reason.title}>
              <div
                className={`group relative flex flex-col gap-4 rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 h-full ${
                  idx % 2 === 0
                    ? 'bg-secondary-500 text-white'
                    : 'border border-neutral-200 bg-white'
                }`}
              >
                {/* Number badge */}
                <span
                  className={`text-7xl font-bold leading-none select-none ${
                    idx % 2 === 0 ? 'text-white/8' : 'text-neutral-900/6'
                  } absolute right-6 top-4`}
                >
                  {String(idx + 1).padStart(2, '0')}
                </span>

                {/* Check icon */}
                <LucideCheckCircle2
                  className={`text-3xl ${
                    idx % 2 === 0 ? 'text-primary-400' : 'text-secondary-500'
                  }`}
                />

                <h3
                  className={`text-2xl font-bold ${
                    idx % 2 === 0 ? 'text-white' : 'text-neutral-900'
                  }`}
                >
                  {reason.title}
                </h3>

                <p
                  className={`text-base leading-relaxed ${
                    idx % 2 === 0 ? 'text-neutral-300' : 'text-neutral-500'
                  }`}
                >
                  {reason.description}
                </p>
              </div>
            </RevealItem>
          ))}
        </ScrollReveal>
      </div>
    </section>
  )
}
